// frontend/src/pages/Register.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, ArrowRight, Phone } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";

const Register = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth(); // assumes AuthContext provides signUp(name, email, password)

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim()) return "Please enter your email.";
    if (!form.password) return "Please enter a password.";
    if (form.password.length < 6) return "Password must be at least 6 characters.";
    if (form.password !== form.confirmPassword) return "Passwords do not match.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    try {
      setLoading(true);
      await signUp(form.name.trim(), form.email.trim(), form.password);
      toast.success("Account created successfully!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* ✅ SEO for Register (Noindex recommended) */}
      <SEO
        title="Create Account | OJOVEST"
        description="Create your OJOVEST account to access premium loan applications, elite credit card offers, and your client dashboard."
        path="/register"
        noindex={true}
      />

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#354F37] font-bold text-xl">
          OJOVEST
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 pb-16">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-[#222222] mb-2">Create your account</h1>
          <p className="text-[#444444] mb-8">
            Start your premium application journey with OJOVEST.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-[#222222] mb-2">
                Full Name
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <User size={18} />
                </span>
                <input
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="Your full name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-[#222222] mb-2">
                Email
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Mail size={18} />
                </span>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="you@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-[#222222] mb-2">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={18} />
                </span>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="Create a password"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Minimum 6 characters recommended.
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-[#222222] mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={18} />
                </span>
                <input
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="Re-enter password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#354F37] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#426045] transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Creating..." : "Create Account"}
              <ArrowRight size={18} />
            </button>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-[#444444]">
                Already have an account?{" "}
                <Link to="/login" className="text-[#354F37] font-semibold hover:underline">
                  Login
                </Link>
              </p>

              <a
                href="https://wa.me/918651035362?text=Hi%20OJOVEST%20-%20I%20need%20help%20creating%20my%20account."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#354F37] hover:underline"
              >
                <Phone size={16} /> Help on WhatsApp
              </a>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center text-sm text-[#444444]">
          By creating an account you agree to our{" "}
          <Link to="/" className="text-[#354F37] font-semibold hover:underline">
            terms & privacy policy
          </Link>
          .
        </div>
      </div>
    </div>
  );
};

export default Register;
